import { WHOLE_DAY_LABEL, readableDate, toTwelveHour, wholeDayTimes } from '@pams/shared';
import type { ArrangementDraft } from '@/components/intake/draft';

/**
 * `Whole day` · `08:00–20:00` · `2 shifts`, plus the dates. Pure, so the card stays cheap.
 *
 * ⚠️ [C5b §3e] ITS OWN MODULE, MOVED OUT OF `DutyPointCard.tsx` so `timePhrase.test.ts` can pin
 * `docs/status-matrix.md`'s `web.builder.summary` cells — a component file that also exports a
 * function breaks React fast refresh, and `react-refresh/only-export-components` refuses it.
 */
export const timePhrase = (draft: ArrangementDraft): string => {
  /*
   * ⚠️ [amended D4.2] THE APP'S FORMATTER, NOT A RAW ISO STRING. This printed `2026-08-16`
   * directly above a picker showing `16 Aug 2026`. `readableDate` is the function every
   * derived arrangement title already goes through, so the summary and the title on the page
   * above it now agree by construction rather than by coincidence.
   */
  const on = (date: string): string => readableDate(date) ?? date;
  const first = draft.shifts[0];
  /*
   * ⚠️ [C3a §6] THE REPORTING TIME LEADS WHERE THERE IS NO SHIFT TO STATE — AND THE SUBSTITUTION
   * IS **INSIDE** THE WHOLE-DAY BRANCH, NEVER BESIDE IT.
   *
   * On a shift-bearing card the form may still hold a time — typed before a switch to shifts, or
   * seeded from a pre-C5b record — and reading it outside this branch would turn
   * `8:00 AM – 8:00 PM` into `From 8:00 AM`. That is the same trap `slotTimeLabel`'s header
   * records, one layer up.
   *
   * ⚠️ [amended C5b §3e] AND WHOLE-DAY IS THE TIMING KIND ALONE. It read `… || !first`, so a shift
   * card whose one row had just been cleared fell into this branch and summarised the form's stale
   * reporting time as `From 9:00 AM` — one of the reported routes to a 9 AM. A shift card with no
   * row now reads `0 shifts`, the existing template, and never borrows the whole-day sentence.
   */
  const wholeDay = draft.timingKind === 'NO_FIXED_TIME';
  /* [STAND-DOWN] `3:00 PM–9:00 PM` when a stand-down time is set, `From 3:00 PM` when it is not. */
  const time = wholeDay
    ? (wholeDayTimes(draft.reportingTime, draft.standDownTime) ?? WHOLE_DAY_LABEL)
    : draft.shifts[1] || !first
      ? `${String(draft.shifts.length)} shifts`
      : `${toTwelveHour(first.startTime)} – ${toTwelveHour(first.endTime)}`;

  const when =
    draft.recurrenceKind === 'ONE_DAY'
      ? on(draft.startDate || draft.paDate)
      : draft.recurrenceKind === 'WEEKDAYS'
        ? `${String(draft.weekdays.length)} day(s) a week`
        : `${on(draft.startDate || draft.paDate)} – ${on(draft.endDate)}`;

  return when ? `${time} · ${when}` : time;
};
